import React, { useState, useEffect } from 'react';
import {
  Briefcase,
  Calendar,
  MapPin,
  Clock,
  Tag,
  Save,
  Loader2,
  AlertCircle,
  Check,
  Plus,
  X,
  Construction
} from 'lucide-react';
import { useAuth } from '../../../../contexts/AuthContext';
import { settingsService } from '../../../../services/settingsService';

const DAYS_OF_WEEK = [
  { key: 'monday', label: 'Mon' },
  { key: 'tuesday', label: 'Tue' },
  { key: 'wednesday', label: 'Wed' },
  { key: 'thursday', label: 'Thu' },
  { key: 'friday', label: 'Fri' },
  { key: 'saturday', label: 'Sat' },
  { key: 'sunday', label: 'Sun' }
];

const JOB_CATEGORIES = [
  'Cleaning',
  'Security',
  'Electrical',
  'Plumbing',
  'Landscaping',
  'Painting',
  'Carpentry',
  'General Maintenance',
  'HVAC',
  'IT Support',
  'Delivery',
  'Catering',
  'Events',
  'Other'
];

const JobPreferencesTab = () => {
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Availability state
  const [availability, setAvailability] = useState({
    monday: { available: true, start: '08:00', end: '18:00' },
    tuesday: { available: true, start: '08:00', end: '18:00' },
    wednesday: { available: true, start: '08:00', end: '18:00' },
    thursday: { available: true, start: '08:00', end: '18:00' },
    friday: { available: true, start: '08:00', end: '18:00' },
    saturday: { available: false, start: '09:00', end: '17:00' },
    sunday: { available: false, start: '09:00', end: '17:00' }
  });

  // Job preferences state
  const [preferences, setPreferences] = useState({
    preferred_categories: [],
    skills: [],
    max_travel_km: 50,
    max_jobs_per_day: 3
  });

  // Skill input
  const [newSkill, setNewSkill] = useState('');

  useEffect(() => {
    loadPreferences();
  }, []);

  const loadPreferences = async () => {
    try {
      setLoading(true);
      setError(null);

      try {
        const { data } = await settingsService.getUserProfile();
        if (data?.job_preferences) {
          setPreferences(prev => ({ ...prev, ...data.job_preferences }));
        }
        if (data?.availability) {
          setAvailability(prev => ({ ...prev, ...data.availability }));
        }
      } catch (e) {
        console.warn('Job preferences not available:', e.message);
      }
    } catch (err) {
      console.error('Error loading preferences:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      await settingsService.updateUserProfile({
        job_preferences: preferences,
        availability: availability
      });

      setSuccess('Preferences saved successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error saving preferences:', err);
      setError('Failed to save preferences');
    } finally {
      setSaving(false);
    }
  };

  const toggleCategory = (category) => {
    setPreferences(prev => {
      const cats = prev.preferred_categories || [];
      if (cats.includes(category)) {
        return { ...prev, preferred_categories: cats.filter(c => c !== category) };
      } else {
        return { ...prev, preferred_categories: [...cats, category] };
      }
    });
  };

  const addSkill = () => {
    if (newSkill.trim() && !preferences.skills?.includes(newSkill.trim())) {
      setPreferences(prev => ({
        ...prev,
        skills: [...(prev.skills || []), newSkill.trim()]
      }));
      setNewSkill('');
    }
  };

  const removeSkill = (skill) => {
    setPreferences(prev => ({
      ...prev,
      skills: (prev.skills || []).filter(s => s !== skill)
    }));
  };

  const handleSkillKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addSkill();
    }
  };

  const updateDayAvailability = (day, field, value) => {
    setAvailability(prev => ({
      ...prev,
      [day]: { ...prev[day], [field]: value }
    }));
  };

  const Toggle = ({ checked, onChange }) => (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
        checked ? 'bg-primary' : 'bg-border'
      }`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm transition-transform ${
          checked ? 'translate-x-6' : 'translate-x-1'
        }`}
      />
    </button>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Coming Soon Banner */}
      <div className="flex items-start gap-3 p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg">
        <Construction className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-amber-500 font-medium text-sm">Coming Soon</p>
          <p className="text-amber-400/80 text-xs mt-1">
            These preferences are not yet active. In the future, they will be used to match you with relevant jobs and filter your job listings based on your availability, skills, and location preferences.
          </p>
        </div>
      </div>

      {/* Header */}
      <div>
        <h2 className="text-xl font-semibold text-text-primary">Job Preferences</h2>
        <p className="text-text-secondary text-sm mt-1">
          Set your availability and job preferences
        </p>
      </div>

      {/* Alerts */}
      {error && (
        <div className="flex items-center space-x-2 p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
          <AlertCircle size={16} className="text-red-500" />
          <span className="text-red-400 text-sm">{error}</span>
        </div>
      )}
      {success && (
        <div className="flex items-center space-x-2 p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
          <Check size={16} className="text-green-500" />
          <span className="text-green-400 text-sm">{success}</span>
        </div>
      )}

      {/* ==================== AVAILABILITY ==================== */}
      <div className="border-b border-border pb-6">
        <div className="flex items-center space-x-2 mb-4">
          <Calendar size={16} className="text-text-secondary" />
          <h3 className="text-sm font-medium text-text-secondary">Weekly Availability</h3>
        </div>
        <p className="text-text-secondary text-xs mb-4">Set your regular working hours for each day of the week</p>

        <div className="space-y-3">
          {DAYS_OF_WEEK.map(({ key, label }) => (
            <div
              key={key}
              className={`flex items-center justify-between p-3 rounded-lg border transition-colors ${
                availability[key]?.available
                  ? 'bg-surface border-border'
                  : 'bg-background border-border'
              }`}
            >
              <div className="flex items-center space-x-4">
                <div className="w-12">
                  <span className={`text-sm font-medium ${availability[key]?.available ? 'text-text-primary' : 'text-text-secondary'}`}>
                    {label}
                  </span>
                </div>
                <Toggle
                  checked={availability[key]?.available}
                  onChange={(val) => updateDayAvailability(key, 'available', val)}
                />
              </div>

              {availability[key]?.available && (
                <div className="flex items-center space-x-2">
                  <input
                    type="time"
                    value={availability[key]?.start || '08:00'}
                    onChange={(e) => updateDayAvailability(key, 'start', e.target.value)}
                    className="px-3 py-1.5 bg-background border border-border rounded-lg text-text-primary text-sm focus:outline-none focus:border-primary"
                  />
                  <span className="text-text-secondary">to</span>
                  <input
                    type="time"
                    value={availability[key]?.end || '18:00'}
                    onChange={(e) => updateDayAvailability(key, 'end', e.target.value)}
                    className="px-3 py-1.5 bg-background border border-border rounded-lg text-text-primary text-sm focus:outline-none focus:border-primary"
                  />
                </div>
              )}

              {!availability[key]?.available && (
                <span className="text-text-secondary text-sm">Not available</span>
              )}
            </div>
          ))}
        </div>

        {/* Max jobs per day */}
        <div className="mt-4 p-4 bg-surface border border-border rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-text-primary text-sm font-medium">Maximum Jobs per Day</p>
              <p className="text-text-secondary text-xs">Limit the number of jobs you can be assigned per day</p>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setPreferences(prev => ({ ...prev, max_jobs_per_day: Math.max(1, (prev.max_jobs_per_day || 3) - 1) }))}
                className="w-8 h-8 bg-background border border-border rounded-lg text-text-primary hover:bg-surface-light transition-colors"
              >
                -
              </button>
              <span className="w-12 text-center text-text-primary font-medium">
                {preferences.max_jobs_per_day || 3}
              </span>
              <button
                onClick={() => setPreferences(prev => ({ ...prev, max_jobs_per_day: Math.min(10, (prev.max_jobs_per_day || 3) + 1) }))}
                className="w-8 h-8 bg-background border border-border rounded-lg text-text-primary hover:bg-surface-light transition-colors"
              >
                +
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ==================== JOB TYPES ==================== */}
      <div className="border-b border-border pb-6">
        <div className="flex items-center space-x-2 mb-4">
          <Briefcase size={16} className="text-text-secondary" />
          <h3 className="text-sm font-medium text-text-secondary">Preferred Job Types</h3>
        </div>
        <p className="text-text-secondary text-xs mb-4">Select the types of jobs you prefer to work on</p>

        <div className="flex flex-wrap gap-2">
          {JOB_CATEGORIES.map((category) => {
            const isSelected = preferences.preferred_categories?.includes(category);
            return (
              <button
                key={category}
                onClick={() => toggleCategory(category)}
                className={`px-3 py-1.5 rounded-full text-sm transition-colors ${
                  isSelected
                    ? 'bg-primary text-white'
                    : 'bg-surface text-text-secondary hover:bg-surface-light hover:text-text-primary border border-border'
                }`}
              >
                {category}
              </button>
            );
          })}
        </div>
      </div>

      {/* ==================== SKILLS ==================== */}
      <div className="border-b border-border pb-6">
        <div className="flex items-center space-x-2 mb-4">
          <Tag size={16} className="text-text-secondary" />
          <h3 className="text-sm font-medium text-text-secondary">Skills & Tags</h3>
        </div>
        <p className="text-text-secondary text-xs mb-4">Add skills that help match you to relevant jobs</p>

        {/* Skill Input */}
        <div className="flex space-x-2 mb-4">
          <input
            type="text"
            value={newSkill}
            onChange={(e) => setNewSkill(e.target.value)}
            onKeyDown={handleSkillKeyDown}
            className="flex-1 px-4 py-2 bg-surface border border-border rounded-lg text-text-primary focus:outline-none focus:border-primary"
            placeholder="e.g., CCTV Installation, First Aid, Forklift License"
          />
          <button
            onClick={addSkill}
            disabled={!newSkill.trim()}
            className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Plus size={18} />
          </button>
        </div>

        {/* Skills List */}
        {preferences.skills?.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {preferences.skills.map((skill) => (
              <span
                key={skill}
                className="inline-flex items-center px-3 py-1.5 bg-surface border border-border rounded-full text-sm text-text-primary"
              >
                {skill}
                <button
                  onClick={() => removeSkill(skill)}
                  className="ml-2 text-text-secondary hover:text-red-400 transition-colors"
                >
                  <X size={14} />
                </button>
              </span>
            ))}
          </div>
        )}

        {(!preferences.skills || preferences.skills.length === 0) && (
          <p className="text-text-secondary text-sm italic">No skills added yet</p>
        )}
      </div>

      {/* ==================== LOCATION PREFERENCES ==================== */}
      <div className="border-b border-border pb-6">
        <div className="flex items-center space-x-2 mb-4">
          <MapPin size={16} className="text-text-secondary" />
          <h3 className="text-sm font-medium text-text-secondary">Location Preferences</h3>
        </div>
        <p className="text-text-secondary text-xs mb-4">Set your preferred travel distance for jobs</p>

        <div className="space-y-4">
          <div>
            <label className="block text-text-secondary text-sm mb-2">Maximum Travel Distance</label>
            <div className="flex items-center space-x-4">
              <input
                type="range"
                min="5"
                max="100"
                step="5"
                value={preferences.max_travel_km || 50}
                onChange={(e) => setPreferences(prev => ({ ...prev, max_travel_km: parseInt(e.target.value) }))}
                className="flex-1 h-2 bg-border rounded-lg appearance-none cursor-pointer accent-primary"
              />
              <div className="w-24 px-3 py-2 bg-surface border border-border rounded-lg text-center">
                <span className="text-text-primary font-medium">{preferences.max_travel_km || 50}</span>
                <span className="text-text-secondary text-sm ml-1">km</span>
              </div>
            </div>
            <p className="text-text-secondary text-xs mt-2">
              You'll only see jobs within {preferences.max_travel_km || 50} km of your location
            </p>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-6 py-2 bg-gradient-to-r from-orange-500 to-orange-600 text-white rounded-lg font-medium hover:from-orange-600 hover:to-orange-700 transition-colors disabled:opacity-50 flex items-center"
        >
          {saving ? (
            <>
              <Loader2 size={16} className="animate-spin mr-2" />
              Saving...
            </>
          ) : (
            <>
              <Save size={16} className="mr-2" />
              Save Preferences
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default JobPreferencesTab;
